// Client-side labels for all profile options. Backend is source of truth for validation.
// Two locales: it, en. Labels for `values`/`interests` mirror the backend option keys.

const L = {
  it: {
    gender: {
      woman: "Donna", man: "Uomo", non_binary: "Non binario",
      other: "Altro", prefer_not_to_say: "Preferisco non dirlo",
    },
    sexual_orientation: {
      straight: "Etero", gay: "Gay", lesbian: "Lesbica", bisexual: "Bisessuale",
      pansexual: "Pansessuale", asexual: "Asessuale",
      other: "Altro", prefer_not_to_say: "Preferisco non dirlo",
    },
    interested_in: { woman: "Donne", man: "Uomini", non_binary: "Persone non binarie", other: "Altro" },
    relationship_goals: {
      serious: "Relazione seria", long_term: "Lungo termine", marriage: "Matrimonio",
      dating: "Uscire insieme", casual_dating: "Frequentazioni leggere",
      friendship: "Amicizia", new_connections: "Nuove conoscenze",
      open_to_see: "Aperto a vedere dove porta", other: "Altro",
    },
    interests: {
      travel: "Viaggi", music: "Musica", concerts: "Concerti", cinema: "Cinema",
      tv_series: "Serie TV", reading: "Lettura", cooking: "Cucinare",
      restaurants: "Ristoranti", nature: "Natura", hiking: "Escursioni",
      fitness: "Fitness", running: "Corsa", cycling: "Ciclismo",
      motorcycles: "Moto", cars: "Auto", photography: "Fotografia", art: "Arte",
      dancing: "Ballo", theatre: "Teatro", gaming: "Gaming", technology: "Tecnologia",
      entrepreneurship: "Imprenditorialità", business: "Business", finance: "Finanza",
      animals: "Animali", dogs: "Cani", cats: "Gatti", gardening: "Giardinaggio",
      diy: "Fai-da-te", fishing: "Pesca", camping: "Campeggio", sports: "Sport",
      yoga: "Yoga", meditation: "Meditazione", spirituality: "Spiritualità",
      history: "Storia", politics: "Politica", philosophy: "Filosofia",
      personal_development: "Crescita personale", volunteering: "Volontariato",
      fashion: "Moda", food: "Cucina", wine: "Vino", beach: "Mare",
      mountains: "Montagna", sailing: "Vela", skiing: "Sci",
    },
    smoking: { never: "Mai", occasionally: "Occasionalmente", regularly: "Regolarmente", prefer_not_to_say: "Preferisco non dirlo" },
    alcohol: { never: "Mai", occasionally: "Occasionalmente", socially: "Socialmente", regularly: "Regolarmente", prefer_not_to_say: "Preferisco non dirlo" },
    pets: { have_pets: "Ho animali", love_pets: "Amo gli animali", no_pets: "Nessun animale", prefer_not_to_say: "Preferisco non dirlo" },
    lifestyle_type: { active: "Attivo", balanced: "Equilibrato", relaxed: "Rilassato", work_focused: "Focalizzato sul lavoro", other: "Altro" },
    diet: { omnivore: "Onnivoro", vegetarian: "Vegetariano", vegan: "Vegano", other: "Altro", prefer_not_to_say: "Preferisco non dirlo" },
    relationship_status: { single: "Single", divorced: "Divorziato/a", widowed: "Vedovo/a", separated: "Separato/a", other: "Altro" },
    children: { no_children: "Nessun figlio", have_children: "Ho figli", prefer_not_to_say: "Preferisco non dirlo" },
    wants_children: { definitely: "Sicuramente", probably: "Probabilmente", maybe: "Forse", no: "No", prefer_not_to_say: "Preferisco non dirlo" },
    open_to_children: { yes: "Sì", no: "No", not_sure: "Non sono sicuro/a" },
    education: { high_school: "Diploma", professional: "Qualifica professionale", bachelor: "Laurea triennale", master: "Laurea magistrale", doctorate: "Dottorato", other: "Altro", prefer_not_to_say: "Preferisco non dirlo" },
    religion: {
      christian: "Cristiano", catholic: "Cattolico", orthodox: "Ortodosso",
      protestant: "Protestante", muslim: "Musulmano", jewish: "Ebreo",
      hindu: "Induista", buddhist: "Buddista",
      spiritual_not_religious: "Spirituale, non religioso",
      agnostic: "Agnostico", atheist: "Ateo", other: "Altro",
      prefer_not_to_say: "Preferisco non dirlo",
    },
    religion_importance: { very_important: "Molto importante", important: "Importante", somewhat_important: "Abbastanza importante", not_important: "Non importante", prefer_not_to_say: "Preferisco non dirlo" },
    covid_perspective: {
      not_vaccinated: "Non vaccinato",
      vaccinated: "Vaccinato",
      vaccinated_changed_mind: "Vaccinato, poi ho cambiato idea",
      prefer_not_to_say: "Preferisco non dirlo",
      other: "Altro",
    },
    covid_motivations: {
      share_worldview: "Condivido la visione della community",
      personal_freedom: "La libertà personale è importante per me",
      independent_thinking: "Apprezzo il pensiero indipendente",
      different_pandemic_experience: "Ho vissuto la pandemia diversamente",
      similar_values: "Cerco persone con valori simili",
      curious: "Sono curioso della community",
      meet_special_someone: "Vorrei incontrare qualcuno di speciale",
      other: "Altro",
    },
    values: {
      freedom: "Libertà", family: "Famiglia", loyalty: "Lealtà", honesty: "Onestà",
      authenticity: "Autenticità", independence: "Indipendenza", privacy: "Privacy",
      personal_responsibility: "Responsabilità personale", open_mindedness: "Apertura mentale",
      critical_thinking: "Pensiero critico", kindness: "Gentilezza", spirituality: "Spiritualità",
      adventure: "Avventura", stability: "Stabilità", security: "Sicurezza",
      ambition: "Ambizione", simplicity: "Semplicità", nature: "Natura",
      community: "Comunità", creativity: "Creatività", personal_growth: "Crescita personale",
      traditional_values: "Valori tradizionali", modern_values: "Valori moderni",
      equality: "Uguaglianza", respect: "Rispetto",
      entrepreneurship: "Imprenditorialità", intellectual_curiosity: "Curiosità intellettuale",
    },
    communication_style: {
      deep_conversations: "Conversazioni profonde", humour: "Umorismo",
      spontaneous: "Spontaneo", voice_messages: "Messaggi vocali",
      frequent: "Frequente", occasional: "Occasionale", in_person: "In persona",
    },
    languages: {
      it: "Italiano", en: "Inglese", de: "Tedesco", fr: "Francese", es: "Spagnolo",
      pt: "Portoghese", nl: "Olandese", pl: "Polacco", ro: "Rumeno", el: "Greco",
      hu: "Ungherese", cs: "Ceco", sk: "Slovacco", sv: "Svedese", no: "Norvegese",
      da: "Danese", fi: "Finlandese", ru: "Russo", uk: "Ucraino", tr: "Turco", other: "Altra",
    },
  },
  en: {
    gender: { woman: "Woman", man: "Man", non_binary: "Non-binary", other: "Other", prefer_not_to_say: "Prefer not to say" },
    sexual_orientation: { straight: "Straight", gay: "Gay", lesbian: "Lesbian", bisexual: "Bisexual", pansexual: "Pansexual", asexual: "Asexual", other: "Other", prefer_not_to_say: "Prefer not to say" },
    interested_in: { woman: "Women", man: "Men", non_binary: "Non-binary people", other: "Other" },
    relationship_goals: {
      serious: "Serious relationship", long_term: "Long-term relationship", marriage: "Marriage",
      dating: "Dating", casual_dating: "Casual dating", friendship: "Friendship",
      new_connections: "New connections", open_to_see: "Open to see where it goes", other: "Other",
    },
    interests: {
      travel: "Travel", music: "Music", concerts: "Concerts", cinema: "Cinema",
      tv_series: "TV series", reading: "Reading", cooking: "Cooking",
      restaurants: "Restaurants", nature: "Nature", hiking: "Hiking",
      fitness: "Fitness", running: "Running", cycling: "Cycling",
      motorcycles: "Motorcycles", cars: "Cars", photography: "Photography", art: "Art",
      dancing: "Dancing", theatre: "Theatre", gaming: "Gaming", technology: "Technology",
      entrepreneurship: "Entrepreneurship", business: "Business", finance: "Finance",
      animals: "Animals", dogs: "Dogs", cats: "Cats", gardening: "Gardening",
      diy: "DIY", fishing: "Fishing", camping: "Camping", sports: "Sports",
      yoga: "Yoga", meditation: "Meditation", spirituality: "Spirituality",
      history: "History", politics: "Politics", philosophy: "Philosophy",
      personal_development: "Personal development", volunteering: "Volunteering",
      fashion: "Fashion", food: "Food", wine: "Wine", beach: "Beach",
      mountains: "Mountains", sailing: "Sailing", skiing: "Skiing",
    },
    smoking: { never: "Never", occasionally: "Occasionally", regularly: "Regularly", prefer_not_to_say: "Prefer not to say" },
    alcohol: { never: "Never", occasionally: "Occasionally", socially: "Socially", regularly: "Regularly", prefer_not_to_say: "Prefer not to say" },
    pets: { have_pets: "Have pets", love_pets: "Love pets", no_pets: "No pets", prefer_not_to_say: "Prefer not to say" },
    lifestyle_type: { active: "Active", balanced: "Balanced", relaxed: "Relaxed", work_focused: "Work-focused", other: "Other" },
    diet: { omnivore: "Omnivore", vegetarian: "Vegetarian", vegan: "Vegan", other: "Other", prefer_not_to_say: "Prefer not to say" },
    relationship_status: { single: "Single", divorced: "Divorced", widowed: "Widowed", separated: "Separated", other: "Other" },
    children: { no_children: "No children", have_children: "Have children", prefer_not_to_say: "Prefer not to say" },
    wants_children: { definitely: "Definitely", probably: "Probably", maybe: "Maybe", no: "No", prefer_not_to_say: "Prefer not to say" },
    open_to_children: { yes: "Yes", no: "No", not_sure: "Not sure" },
    education: { high_school: "High school", professional: "Professional qualification", bachelor: "Bachelor's degree", master: "Master's degree", doctorate: "Doctorate", other: "Other", prefer_not_to_say: "Prefer not to say" },
    religion: {
      christian: "Christian", catholic: "Catholic", orthodox: "Orthodox",
      protestant: "Protestant", muslim: "Muslim", jewish: "Jewish",
      hindu: "Hindu", buddhist: "Buddhist",
      spiritual_not_religious: "Spiritual but not religious",
      agnostic: "Agnostic", atheist: "Atheist", other: "Other",
      prefer_not_to_say: "Prefer not to say",
    },
    religion_importance: { very_important: "Very important", important: "Important", somewhat_important: "Somewhat important", not_important: "Not important", prefer_not_to_say: "Prefer not to say" },
    covid_perspective: {
      not_vaccinated: "Not vaccinated",
      vaccinated: "Vaccinated",
      vaccinated_changed_mind: "Vaccinated and later changed my mind",
      prefer_not_to_say: "Prefer not to say",
      other: "Other",
    },
    covid_motivations: {
      share_worldview: "I share the community's worldview",
      personal_freedom: "Personal freedom is important to me",
      independent_thinking: "I value independent thinking",
      different_pandemic_experience: "I had a different experience during the pandemic",
      similar_values: "I'm looking for people with similar values",
      curious: "I'm curious about the community",
      meet_special_someone: "I simply want to meet someone special",
      other: "Other",
    },
    values: {
      freedom: "Freedom", family: "Family", loyalty: "Loyalty", honesty: "Honesty",
      authenticity: "Authenticity", independence: "Independence", privacy: "Privacy",
      personal_responsibility: "Personal responsibility", open_mindedness: "Open-mindedness",
      critical_thinking: "Critical thinking", kindness: "Kindness", spirituality: "Spirituality",
      adventure: "Adventure", stability: "Stability", security: "Security",
      ambition: "Ambition", simplicity: "Simplicity", nature: "Nature",
      community: "Community", creativity: "Creativity", personal_growth: "Personal growth",
      traditional_values: "Traditional values", modern_values: "Modern values",
      equality: "Equality", respect: "Respect",
      entrepreneurship: "Entrepreneurship", intellectual_curiosity: "Intellectual curiosity",
    },
    communication_style: {
      deep_conversations: "Deep conversations", humour: "Humour",
      spontaneous: "Spontaneous", voice_messages: "Voice messages",
      frequent: "Frequent", occasional: "Occasional", in_person: "In-person meetings",
    },
    languages: {
      it: "Italian", en: "English", de: "German", fr: "French", es: "Spanish",
      pt: "Portuguese", nl: "Dutch", pl: "Polish", ro: "Romanian", el: "Greek",
      hu: "Hungarian", cs: "Czech", sk: "Slovak", sv: "Swedish", no: "Norwegian",
      da: "Danish", fi: "Finnish", ru: "Russian", uk: "Ukrainian", tr: "Turkish", other: "Other",
    },
  },
};

export function optionLabel(lang, kind, key) {
  if (!key) return "";
  return L[lang]?.[kind]?.[key] || key;
}

export function useOptionLabel() {
  // Deliberately not using hooks here to keep it a plain util; components
  // consuming this should pass lang from useI18n.
  return optionLabel;
}

export const LABELS = L;
